import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabaseClient';
import { getActiveTeamId } from '../activeTeam';
import type {
  Invite,
  Message,
  PendingAttachment,
  Profile,
  Reminder,
  ReminderTag,
  Stage,
  Task,
  TaskEstado,
  TeamInfo,
} from './types';

// Módulo único de acceso a Supabase para el feature `tareas`. Los componentes
// no deben importar `supabaseClient` directamente — sólo este módulo y storage.ts.

const TASK_FIELDS = `
  id, asunto, estado, created_by, assigned_to, fecha_vencimiento, assignee_due,
  created_at, updated_at, stage_id, team_id,
  assignee:profiles!tasks_assigned_to_fkey(id, nombre, color),
  creator:profiles!tasks_created_by_fkey(id, nombre, color)
`;

const MESSAGE_FIELDS = `
  id, task_id, author_id, body, created_at, edited_at, is_private,
  author:profiles!messages_author_id_fkey(id, nombre, color),
  attachments(id, storage_path, file_name, mime_type, size_bytes),
  reactions:message_reactions(emoji, user_id)
`;

// La cadena de query builders de Supabase tiene tipos anidados que aportan
// poco si los tipamos paso a paso — el contrato público (retornos de hooks
// y mutaciones) sí está tipado fuerte.
type QueryFilter = (q: any) => any;

type TasksListResult = {
  tasks: Task[];
  loading: boolean;
  refetch: () => Promise<void>;
};

// --- Equipos (espacios de trabajo / tenant) e invitaciones ---

// Crea un equipo nuevo y mete al usuario actual en él (solo si no tiene equipo).
export async function createTeam(nombre: string): Promise<TeamInfo> {
  const { data, error } = await supabase.rpc('create_team', { p_nombre: nombre });
  if (error) throw error;
  return data as TeamInfo;
}

// Une al usuario actual a un equipo validando el token del enlace de invitación.
export async function joinTeamByToken(teamId: string, token: string): Promise<TeamInfo> {
  const { data, error } = await supabase.rpc('join_team_by_token', {
    p_team_id: teamId,
    p_token: token,
  });
  if (error) throw error;
  return data as TeamInfo;
}

// Acepta una invitación por email (el email debe coincidir con el del usuario).
export async function acceptInvite(inviteId: string): Promise<TeamInfo> {
  const { data, error } = await supabase.rpc('accept_invite', { p_invite_id: inviteId });
  if (error) throw error;
  return data as TeamInfo;
}

// Nombre del equipo para la landing de invitación (sin ser miembro aún).
export async function teamPreview(teamId: string, token: string): Promise<string | null> {
  const { data, error } = await supabase.rpc('team_preview', {
    p_team_id: teamId,
    p_token: token,
  });
  if (error) throw error;
  return (data as string | null) ?? null;
}

// Crea una invitación por email (solo el owner del equipo) y dispara el correo.
export async function createInvite(email: string): Promise<Invite> {
  const { data, error } = await supabase.rpc('create_invite', { p_email: email });
  if (error) throw error;
  const invite = data as Invite;
  // Envío de email best-effort: no bloquea el alta de la invitación.
  try {
    await supabase.functions.invoke('send-invite-email', { body: { invite_id: invite.id } });
  } catch {
    /* el correo es opcional; el enlace de invitación sigue sirviendo */
  }
  return invite;
}

export async function deleteInvite(inviteId: string): Promise<void> {
  const { error } = await supabase.from('invites').delete().eq('id', inviteId);
  if (error) throw error;
}

export async function renameTeam(teamId: string, nombre: string): Promise<void> {
  const { error } = await supabase.from('teams').update({ nombre: nombre.trim() }).eq('id', teamId);
  if (error) throw error;
}

// Guarda (o limpia con null) la URL pública del logo del equipo.
export async function setTeamLogo(teamId: string, logoUrl: string | null): Promise<void> {
  const { error } = await supabase.from('teams').update({ logo_url: logoUrl }).eq('id', teamId);
  if (error) throw error;
}

// Rota el token del enlace de invitación (invalida los enlaces antiguos).
export async function rotateJoinToken(teamId: string): Promise<string> {
  const { data, error } = await supabase
    .from('teams')
    .update({ join_token: crypto.randomUUID() })
    .eq('id', teamId)
    .select('join_token')
    .single();
  if (error) throw error;
  return (data as { join_token: string }).join_token;
}

// Info del equipo del usuario (RLS lo limita al propio). Para ajustes de equipo.
export function useTeamInfo(): { team: TeamInfo | null; loading: boolean; refetch: () => Promise<void> } {
  const [team, setTeam] = useState<TeamInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchTeam = useCallback(async () => {
    const active = getActiveTeamId();
    if (!active) { setTeam(null); setLoading(false); return; }
    const { data } = await supabase
      .from('teams')
      .select('id, nombre, owner_id, join_token, logo_url, created_at')
      .eq('id', active)
      .maybeSingle();
    setTeam((data as TeamInfo | null) ?? null);
    setLoading(false);
  }, []);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);
  return { team, loading, refetch: fetchTeam };
}

// Invitaciones pendientes del equipo (para ajustes).
export function useInvites(): { invites: Invite[]; loading: boolean; refetch: () => Promise<void> } {
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    const active = getActiveTeamId();
    if (!active) { setInvites([]); setLoading(false); return; }
    // Todas las invitaciones del equipo (pendientes + aceptadas) para mostrar
    // su estado. RLS las limita al propio equipo.
    const { data } = await supabase
      .from('invites')
      .select('id, team_id, email, invited_by, created_at, expires_at, accepted_at')
      .eq('team_id', active)
      .order('created_at', { ascending: false });
    setInvites((data ?? []) as Invite[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
    const ch = supabase
      .channel(`invites-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'invites' }, () => fetchAll())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [fetchAll]);

  return { invites, loading, refetch: fetchAll };
}

// --- Perfil propio ---
export async function updateMyProfile(
  userId: string,
  patch: { nombre?: string; color?: string },
): Promise<void> {
  const out: Record<string, unknown> = {};
  if (patch.nombre !== undefined) out.nombre = patch.nombre.trim();
  if (patch.color !== undefined) out.color = patch.color;
  if (Object.keys(out).length === 0) return;
  const { error } = await supabase.from('profiles').update(out).eq('id', userId);
  if (error) throw error;
}

// --- Agenda (tareas personales + hilos con fecha de vencimiento) ---

export interface CreateReminderInput {
  titulo: string;
  fecha_vencimiento: string;
  nota?: string | null;
  // Origen opcional cuando se crea desde una nota de un hilo (enlace de vuelta).
  source_task_id?: string | null;
  source_message_id?: string | null;
}

export async function createReminder(
  input: CreateReminderInput,
  userId: string,
  id?: string,
): Promise<Reminder> {
  const row: Record<string, unknown> = {
    user_id: userId,
    titulo: input.titulo.trim(),
    fecha_vencimiento: input.fecha_vencimiento,
    nota: input.nota?.trim() || null,
  };
  if (input.source_task_id) row.source_task_id = input.source_task_id;
  if (input.source_message_id) row.source_message_id = input.source_message_id;
  if (id) row.id = id;
  const { data, error } = await supabase
    .from('reminders')
    .insert(row)
    .select('*')
    .single();
  if (error) throw error;
  return data as Reminder;
}

// --- Adjuntos de recordatorios (privados al dueño) ---
export interface ReminderAttachment {
  id: string;
  reminder_id: string;
  storage_path: string;
  file_name: string;
  mime_type: string | null;
  size_bytes: number | null;
  created_at: string;
}

export async function listReminderAttachments(reminderId: string): Promise<ReminderAttachment[]> {
  const { data, error } = await supabase
    .from('reminder_attachments')
    .select('id, reminder_id, storage_path, file_name, mime_type, size_bytes, created_at')
    .eq('reminder_id', reminderId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as ReminderAttachment[];
}

export async function addReminderAttachment(
  reminderId: string,
  file: { storage_path: string; file_name: string; mime_type: string | null; size_bytes: number | null },
): Promise<ReminderAttachment> {
  const { data, error } = await supabase
    .from('reminder_attachments')
    .insert({ reminder_id: reminderId, ...file })
    .select('id, reminder_id, storage_path, file_name, mime_type, size_bytes, created_at')
    .single();
  if (error) throw error;
  return data as ReminderAttachment;
}

export async function deleteReminderAttachment(id: string): Promise<void> {
  const { error } = await supabase.from('reminder_attachments').delete().eq('id', id);
  if (error) throw error;
}

// --- Preferencias de notificación por email ---
export interface NotificationPrefs {
  notif_mention: boolean;
  notif_assignment: boolean;
  notif_comment: boolean;
  notif_follow: boolean;
  notif_validation: boolean;
  notif_daily_digest: boolean;
  notif_weekly_digest: boolean;
}

export async function getNotificationPrefs(userId: string): Promise<NotificationPrefs> {
  const { data, error } = await supabase
    .from('profiles')
    .select('notif_mention, notif_assignment, notif_comment, notif_follow, notif_validation, notif_daily_digest, notif_weekly_digest')
    .eq('id', userId)
    .single();
  if (error) throw error;
  return data as NotificationPrefs;
}

export async function setNotificationPref(
  userId: string,
  key: keyof NotificationPrefs,
  value: boolean,
): Promise<void> {
  const { error } = await supabase.from('profiles').update({ [key]: value }).eq('id', userId);
  if (error) throw error;
}

export async function updateReminder(
  id: string,
  patch: { titulo?: string; fecha_vencimiento?: string; nota?: string | null },
): Promise<void> {
  const out: Record<string, unknown> = {};
  if (patch.titulo !== undefined) out.titulo = patch.titulo.trim();
  if (patch.fecha_vencimiento !== undefined) out.fecha_vencimiento = patch.fecha_vencimiento;
  if (patch.nota !== undefined) out.nota = patch.nota?.trim() || null;
  if (Object.keys(out).length === 0) return;
  const { error } = await supabase.from('reminders').update(out).eq('id', id);
  if (error) throw error;
}

export async function toggleReminderDone(id: string, done: boolean): Promise<void> {
  const { error } = await supabase
    .from('reminders')
    .update({ done, done_at: done ? new Date().toISOString() : null })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteReminder(id: string): Promise<void> {
  const { error } = await supabase.from('reminders').delete().eq('id', id);
  if (error) throw error;
}

export interface AgendaItem {
  kind: 'reminder' | 'thread';
  id: string;
  fecha: string;
  reminder?: Reminder;
  task?: Task;
}

export interface AgendaResult {
  vencidas: AgendaItem[];
  hoy: AgendaItem[];
  proximas: AgendaItem[];
  counts: { vencidas: number; hoy: number; proximas: number };
  loading: boolean;
  refetch: () => Promise<void>;
}

// Normaliza una fila de reminders con el conteo embebido reminder_attachments(count)
// y las tags embebidas reminder_tags(tag:tags(...)).
function mapReminderRow(row: unknown): Reminder {
  const r = row as Reminder & {
    reminder_attachments?: Array<{ count: number }>;
    reminder_tags?: Array<{ tag: ReminderTag | null }>;
  };
  const count = Array.isArray(r.reminder_attachments) ? (r.reminder_attachments[0]?.count ?? 0) : 0;
  const tags = (r.reminder_tags ?? [])
    .map((rt) => rt.tag)
    .filter((t): t is ReminderTag => t != null)
    .sort((a, b) => a.nombre.localeCompare(b.nombre));
  const { reminder_attachments, reminder_tags, ...rest } = r;
  void reminder_attachments;
  void reminder_tags;
  return { ...rest, attachments_count: count, tags } as Reminder;
}

// Fecha local de hoy como 'YYYY-MM-DD' (las fechas de vencimiento son DATE).
function localToday(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// Agenda personal: une mis tareas personales (reminders, pendientes) con los
// hilos en curso que me incumben (asignados a mí o creados por mí) que tengan
// fecha de vencimiento. Agrupado en vencidas / hoy / próximas.
export function useAgenda(userId: string | null | undefined): AgendaResult {
  const [items, setItems] = useState<AgendaItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!userId) {
      setItems([]);
      setLoading(false);
      return;
    }
    const active = getActiveTeamId();
    if (!active) { setItems([]); setLoading(false); return; }
    const [{ data: rem }, { data: thr }] = await Promise.all([
      supabase
        .from('reminders')
        .select('*, reminder_attachments(count), reminder_tags(tag:tags(id, nombre, color))')
        .eq('user_id', userId)
        .eq('team_id', active)
        .eq('done', false),
      supabase
        .from('tasks')
        .select(TASK_FIELDS)
        .eq('team_id', active)
        .eq('estado', 'en_curso')
        .or(`assigned_to.eq.${userId},created_by.eq.${userId}`),
    ]);

    const out: AgendaItem[] = [];
    ((rem ?? []) as unknown[]).forEach((row) => {
      const r = mapReminderRow(row);
      out.push({ kind: 'reminder', id: r.id, fecha: r.fecha_vencimiento, reminder: r });
    });
    ((thr ?? []) as unknown as Task[]).forEach((t) => {
      // Fecha efectiva: si soy el asignado y tengo fecha personal, esa manda;
      // si no, la del asignador. El asignador ve siempre la suya.
      const isMine = t.assigned_to === userId;
      const efectiva = (isMine && t.assignee_due) ? t.assignee_due : t.fecha_vencimiento;
      if (!efectiva) return;
      // En la Agenda mostramos la fecha efectiva también en la tarjeta.
      out.push({ kind: 'thread', id: t.id, fecha: efectiva, task: { ...t, fecha_vencimiento: efectiva } });
    });
    out.sort((a, b) => a.fecha.localeCompare(b.fecha));
    setItems(out);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchAll();
    if (!userId) return undefined;
    const active = getActiveTeamId();
    const ch = supabase
      .channel(`agenda-${userId}-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'reminders', filter: `user_id=eq.${userId}` }, () => fetchAll())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks', filter: `team_id=eq.${active}` }, () => fetchAll())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'reminder_tags' }, () => fetchAll())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [fetchAll, userId]);

  const today = localToday();
  const vencidas = items.filter((i) => i.fecha < today);
  const hoy = items.filter((i) => i.fecha === today);
  const proximas = items.filter((i) => i.fecha > today);

  return {
    vencidas,
    hoy,
    proximas,
    counts: { vencidas: vencidas.length, hoy: hoy.length, proximas: proximas.length },
    loading,
    refetch: fetchAll,
  };
}

// Mapa nota → id del recordatorio PENDIENTE que YO tengo sobre ella (el de
// fecha más próxima si hubiera varios). Privado por dueño (RLS): solo refleja
// mis recordatorios. Sirve para encender el reloj y abrir el recordatorio
// existente al pulsarlo (MessageBubble).
export function useMyTaskReminderMsgIds(
  taskId: string | undefined,
  userId: string | null | undefined,
): { byMsg: Map<string, string>; refetch: () => Promise<void> } {
  const [byMsg, setByMsg] = useState<Map<string, string>>(new Map());

  const fetchAll = useCallback(async () => {
    if (!taskId || !userId) { setByMsg(new Map()); return; }
    const { data } = await supabase
      .from('reminders')
      .select('id, source_message_id, fecha_vencimiento')
      .eq('source_task_id', taskId)
      .eq('user_id', userId)
      .eq('done', false)
      .order('fecha_vencimiento', { ascending: true });
    const next = new Map<string, string>();
    ((data ?? []) as Array<{ id: string; source_message_id: string | null }>).forEach((r) => {
      // Primero por fecha asc ⇒ el primero por nota es el de vencimiento más próximo.
      if (r.source_message_id && !next.has(r.source_message_id)) next.set(r.source_message_id, r.id);
    });
    setByMsg(next);
  }, [taskId, userId]);

  useEffect(() => {
    fetchAll();
    if (!taskId || !userId) return undefined;
    const ch = supabase
      .channel(`task-reminders-${taskId}-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'reminders', filter: `user_id=eq.${userId}` }, () => fetchAll())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [fetchAll, taskId, userId]);

  return { byMsg, refetch: fetchAll };
}

// Recordatorios COMPLETADOS del usuario en el equipo activo (para la vista
// "Completadas" de la Agenda). Solo recordatorios; los hilos no entran aquí.
export function useCompletedReminders(userId: string | null | undefined): {
  reminders: Reminder[];
  loading: boolean;
  refetch: () => Promise<void>;
} {
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!userId) { setReminders([]); setLoading(false); return; }
    const active = getActiveTeamId();
    if (!active) { setReminders([]); setLoading(false); return; }
    const { data } = await supabase
      .from('reminders')
      .select('*, reminder_attachments(count), reminder_tags(tag:tags(id, nombre, color))')
      .eq('user_id', userId)
      .eq('team_id', active)
      .eq('done', true)
      .order('done_at', { ascending: false, nullsFirst: false });
    setReminders(((data ?? []) as unknown[]).map(mapReminderRow));
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchAll();
    if (!userId) return undefined;
    const ch = supabase
      .channel(`agenda-done-${userId}-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'reminders', filter: `user_id=eq.${userId}` }, () => fetchAll())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'reminder_tags' }, () => fetchAll())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [fetchAll, userId]);

  return { reminders, loading, refetch: fetchAll };
}

// --- Team ---
export function useTeam(): { team: Profile[]; loading: boolean } {
  const [team, setTeam] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const active = getActiveTeamId();
    if (!active) { setTeam([]); setLoading(false); return; }
    // Miembros del espacio de trabajo ACTIVO (no todos los profiles visibles,
    // que ahora pueden ser de varios espacios compartidos).
    supabase
      .from('team_members')
      .select('profile:profiles!inner(id, nombre, color)')
      .eq('team_id', active)
      .then(({ data }) => {
        if (cancelled) return;
        type Row = { profile: Profile | Profile[] | null };
        const list = ((data ?? []) as unknown as Row[])
          .map((r) => (Array.isArray(r.profile) ? r.profile[0] : r.profile))
          .filter((p): p is Profile => p != null)
          .sort((a, b) => a.nombre.localeCompare(b.nombre));
        setTeam(list);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  return { team, loading };
}

// --- Gestión de miembros (roles Admin / Miembro) ---

export type TeamRole = 'owner' | 'admin' | 'member';
export interface TeamMember extends Profile {
  role: TeamRole;
}

// Miembros del equipo activo CON su rol, para la sección de gestión.
export function useTeamMembers(): {
  members: TeamMember[];
  loading: boolean;
  refetch: () => Promise<void>;
} {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    const active = getActiveTeamId();
    if (!active) { setMembers([]); setLoading(false); return; }
    const { data } = await supabase
      .from('team_members')
      .select('role, profile:profiles!inner(id, nombre, color)')
      .eq('team_id', active);
    type Row = { role: TeamRole; profile: Profile | Profile[] | null };
    const order: Record<TeamRole, number> = { owner: 0, admin: 1, member: 2 };
    const list = ((data ?? []) as unknown as Row[])
      .map((r) => {
        const p = Array.isArray(r.profile) ? r.profile[0] : r.profile;
        return p ? { ...p, role: r.role } : null;
      })
      .filter((m): m is TeamMember => m != null)
      .sort((a, b) => (order[a.role] - order[b.role]) || a.nombre.localeCompare(b.nombre));
    setMembers(list);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
    const ch = supabase
      .channel(`team-members-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'team_members' }, () => fetchAll())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [fetchAll]);

  return { members, loading, refetch: fetchAll };
}

export async function setMemberRole(userId: string, role: 'admin' | 'member'): Promise<void> {
  const { error } = await supabase.rpc('set_member_role', { p_user_id: userId, p_role: role });
  if (error) throw error;
}

export async function removeMember(userId: string): Promise<void> {
  const { error } = await supabase.rpc('remove_member', { p_user_id: userId });
  if (error) throw error;
}

// Abandonar el equipo activo uno mismo (salvo el propietario).
export async function leaveTeam(): Promise<void> {
  const { error } = await supabase.rpc('leave_team');
  if (error) throw error;
}

// Eliminar un espacio de trabajo por completo. Solo el propietario (owner).
// Borra tareas, recordatorios, miembros, invitaciones y grupos del equipo.
export async function deleteTeam(teamId: string): Promise<void> {
  const { error } = await supabase.rpc('delete_team', { p_team_id: teamId });
  if (error) throw error;
}

// --- Planes / facturación (Stripe) ---

export interface TeamPlan {
  plan: 'free' | 'team';
  subscription_status: string | null;
  billing_interval: 'month' | 'year' | null;
  current_period_end: string | null;
}

// Plan del espacio activo (lectura de teams; RLS permite a los miembros leer su fila).
export function useTeamPlan(): { plan: TeamPlan | null; loading: boolean; refetch: () => Promise<void> } {
  const [plan, setPlan] = useState<TeamPlan | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchPlan = useCallback(async () => {
    const active = getActiveTeamId();
    if (!active) { setPlan(null); setLoading(false); return; }
    const { data } = await supabase
      .from('teams')
      .select('plan, subscription_status, billing_interval, current_period_end')
      .eq('id', active).maybeSingle();
    setPlan((data as TeamPlan) ?? null);
    setLoading(false);
  }, []);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);
  return { plan, loading, refetch: fetchPlan };
}

// Inicia Stripe Checkout para mejorar el espacio a Team. Devuelve la URL a la que redirigir.
export async function createCheckoutSession(teamId: string, interval: 'month' | 'year'): Promise<string> {
  const { data, error } = await supabase.functions.invoke('create-checkout-session', {
    body: { team_id: teamId, interval },
  });
  if (error) throw error;
  const url = (data as { url?: string } | null)?.url;
  if (!url) throw new Error('No se pudo iniciar el pago');
  return url;
}

// Abre el Billing Portal de Stripe (gestión de pago, facturas, cancelación). Devuelve la URL.
export async function createPortalSession(teamId: string): Promise<string> {
  const { data, error } = await supabase.functions.invoke('create-portal-session', {
    body: { team_id: teamId },
  });
  if (error) throw error;
  const url = (data as { url?: string } | null)?.url;
  if (!url) throw new Error('No se pudo abrir el portal de facturación');
  return url;
}

// --- Listas ---

// Listar hilos que YO he asignado a una persona concreta. Privacidad:
// no mostramos los que otros le asignaron, aunque la RLS lo permitiría.
export function useTasksByAssignee(assignedTo: string, createdBy: string): TasksListResult {
  return useTasksList(useMemo<QueryFilter>(
    () => (query) => query.eq('assigned_to', assignedTo).eq('created_by', createdBy),
    [assignedTo, createdBy],
  ));
}

export function useAllTasks(): TasksListResult {
  return useTasksList(useMemo<QueryFilter>(() => (query) => query, []));
}

// Tareas sin asignar — canal #General, visible para todo el equipo.
export function useTasksUnassigned(): TasksListResult {
  return useTasksList(useMemo<QueryFilter>(() => (query) => query.is('assigned_to', null), []));
}

// "Por validar": hilos que YO creé y el asignado ha marcado como resuelto.
// Pendientes de que el creador los dé por terminados o los devuelva a en curso.
export function useTasksToValidate(userId: string | null | undefined): TasksListResult {
  return useTasksList(useMemo<QueryFilter>(
    () => (query) => (userId ? query.eq('created_by', userId).eq('estado', 'resuelto') : query),
    [userId],
  ));
}

// Personas a las que el usuario logueado ha asignado hilos. Sustituye a
// "todo el equipo" en la sección TEAM del sidebar: con 50 usuarios sólo
// aparecen aquellos con los que efectivamente colaboras.
export interface MyAssignee {
  profile: Profile;
  openCount: number;
}

export function useMyAssignees(
  userId: string | null | undefined,
): { assignees: MyAssignee[]; loading: boolean } {
  const [assignees, setAssignees] = useState<MyAssignee[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!userId) {
      setAssignees([]);
      setLoading(false);
      return;
    }
    const active = getActiveTeamId();
    if (!active) { setAssignees([]); setLoading(false); return; }
    const { data } = await supabase
      .from('tasks')
      .select(`
        assigned_to,
        estado,
        assignee:profiles!tasks_assigned_to_fkey(id, nombre, color)
      `)
      .eq('team_id', active)
      .eq('created_by', userId)
      .not('assigned_to', 'is', null)
      .neq('assigned_to', userId);

    const rows = (data ?? []) as unknown as Array<{
      assigned_to: string;
      estado: TaskEstado;
      assignee: Profile | null;
    }>;

    const map = new Map<string, MyAssignee>();
    rows.forEach((r) => {
      if (!r.assignee) return;
      const existing = map.get(r.assigned_to);
      if (existing) {
        if (r.estado === 'en_curso') existing.openCount += 1;
      } else {
        map.set(r.assigned_to, {
          profile: r.assignee,
          openCount: r.estado === 'en_curso' ? 1 : 0,
        });
      }
    });

    const list = [...map.values()].sort((a, b) =>
      a.profile.nombre.localeCompare(b.profile.nombre),
    );
    setAssignees(list);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchAll();
    if (!userId) return undefined;
    // Nombre único por instancia — Sidebar y BandejaPage instancian este hook
    // a la vez con el mismo userId, y supabase comparte canales por nombre.
    const ch = supabase
      .channel(`my-assignees-${userId}-${Math.random().toString(36).slice(2, 8)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tasks', filter: `created_by=eq.${userId}` },
        () => fetchAll(),
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [fetchAll, userId]);

  return { assignees, loading };
}

export function useSavedTasks(userId: string | null | undefined): TasksListResult {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!userId) return;
    const active = getActiveTeamId();
    if (!active) { setTasks([]); setLoading(false); return; }
    const { data } = await supabase
      .from('saved_tasks')
      .select(`task:tasks(${TASK_FIELDS}, team_id)`)
      .eq('user_id', userId);
    const rows = (data ?? []) as unknown as Array<{ task: (Task & { team_id?: string }) | null }>;
    // Guardados de TODOS mis espacios (RLS por membresía) → filtro al activo.
    setTasks(rows.map((r) => r.task).filter((t): t is Task => t != null && t.team_id === active));
    setLoading(false);
  }, [userId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  return { tasks, loading, refetch: fetchAll };
}

function useTasksList(applyFilter: QueryFilter): TasksListResult {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    const active = getActiveTeamId();
    if (!active) { setTasks([]); setLoading(false); return; }
    let q = supabase.from('tasks').select(TASK_FIELDS)
      .eq('team_id', active)
      .order('updated_at', { ascending: false });
    q = applyFilter(q);
    const { data } = await q;
    setTasks((data ?? []) as unknown as Task[]);
    setLoading(false);
  }, [applyFilter]);

  useEffect(() => {
    fetchAll();
    const active = getActiveTeamId();
    const ch = supabase
      .channel(`tasks-list-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks', filter: `team_id=eq.${active}` }, () => fetchAll())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [fetchAll]);

  return { tasks, loading, refetch: fetchAll };
}

// --- Búsqueda ---
export async function searchTasks(term: string): Promise<Task[]> {
  const active = getActiveTeamId();
  if (!active) return [];
  const safe = term.replace(/[%,]/g, ' ');
  const [{ data: porAsunto }, { data: porMensaje }] = await Promise.all([
    supabase
      .from('tasks').select(`${TASK_FIELDS}, team_id`)
      .eq('team_id', active)
      .ilike('asunto', `%${safe}%`)
      .order('updated_at', { ascending: false })
      .limit(40),
    supabase
      .from('messages')
      .select(`task_id, task:tasks(${TASK_FIELDS}, team_id)`)
      .ilike('body', `%${safe}%`)
      .limit(40),
  ]);

  const map = new Map<string, Task>();
  const porAsuntoTyped = (porAsunto ?? []) as unknown as Task[];
  porAsuntoTyped.forEach((t) => map.set(t.id, t));
  // La búsqueda por mensaje cruza todos mis espacios (RLS por membresía) → filtro al activo.
  const porMensajeTyped = (porMensaje ?? []) as unknown as Array<{ task: (Task & { team_id?: string }) | null }>;
  porMensajeTyped.forEach((m) => { if (m.task && m.task.team_id === active) map.set(m.task.id, m.task); });
  return [...map.values()];
}

// --- Tarea individual + mensajes con realtime ---
export function useTask(
  taskId: string | undefined,
): { task: Task | null; messages: Message[]; loading: boolean } {
  const [task, setTask] = useState<Task | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!taskId) return;
    let cancelled = false;
    setLoading(true);

    const fetchTask = async () => {
      const { data } = await supabase
        .from('tasks').select(TASK_FIELDS).eq('id', taskId).single();
      if (!cancelled) setTask((data as unknown as Task | null) ?? null);
    };
    const fetchMessages = async () => {
      const { data } = await supabase
        .from('messages')
        .select(MESSAGE_FIELDS)
        .eq('task_id', taskId)
        .order('created_at', { ascending: true });
      if (!cancelled) {
        setMessages((data ?? []) as unknown as Message[]);
        setLoading(false);
      }
    };

    fetchTask();
    fetchMessages();

    const ch = supabase
      .channel(`task-${taskId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `task_id=eq.${taskId}` }, () => fetchMessages())
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'messages', filter: `task_id=eq.${taskId}` }, () => fetchMessages())
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'messages' }, () => fetchMessages())
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'attachments' }, () => fetchMessages())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'message_reactions' }, () => fetchMessages())
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'tasks', filter: `id=eq.${taskId}` }, () => fetchTask())
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(ch);
    };
  }, [taskId]);

  return { task, messages, loading };
}

// --- Carga para edición ---
export interface EditableTask {
  id: string;
  asunto: string;
  assigned_to: string | null;
  fecha_vencimiento: string | null;
  created_by: string;
}

export interface FirstMessage {
  id: string;
  body: string;
}

export async function getTaskForEdit(
  taskId: string,
): Promise<{ task: EditableTask; firstMessage: FirstMessage | null } | null> {
  const { data: task } = await supabase
    .from('tasks')
    .select('id, asunto, assigned_to, fecha_vencimiento, created_by')
    .eq('id', taskId)
    .single();
  if (!task) return null;
  const t = task as EditableTask;

  const { data: msg } = await supabase
    .from('messages')
    .select('id, body')
    .eq('task_id', taskId)
    .eq('author_id', t.created_by)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();

  return { task: t, firstMessage: (msg as FirstMessage | null) ?? null };
}

// --- Mutaciones ---
export interface CreateTaskInput {
  id?: string;
  asunto: string;
  assigned_to: string | null;
  fecha_vencimiento: string | null;
  created_by: string;
}

export async function createTask(input: CreateTaskInput): Promise<{ id: string }> {
  const { id, asunto, assigned_to, fecha_vencimiento, created_by } = input;
  const row: Record<string, unknown> = {
    asunto,
    assigned_to: assigned_to || null,
    fecha_vencimiento: fecha_vencimiento || null,
    created_by,
  };
  if (id) row.id = id;
  const { data, error } = await supabase
    .from('tasks')
    .insert(row)
    .select('id')
    .single();
  if (error) throw error;
  return data as { id: string };
}

export interface SendMessageInput {
  task_id: string;
  author_id: string;
  body: string;
  attachments?: PendingAttachment[];
  // Nota privada: solo la verá el autor (no notifica ni genera menciones).
  is_private?: boolean;
}

export async function sendMessage(input: SendMessageInput): Promise<void> {
  const { task_id, author_id, body, attachments = [], is_private = false } = input;
  const { data: msg, error } = await supabase
    .from('messages')
    .insert({ task_id, author_id, body, is_private })
    .select('id')
    .single();
  if (error) throw error;
  const msgId = (msg as { id: string }).id;

  if (attachments.length > 0) {
    await attachToMessage(msgId, attachments, author_id);
  }

  // Toca updated_at de la tarea para reordenar listas
  await supabase
    .from('tasks')
    .update({ updated_at: new Date().toISOString() })
    .eq('id', task_id);
}

export async function attachToMessage(
  messageId: string,
  attachments: PendingAttachment[],
  uploadedBy: string,
): Promise<void> {
  if (attachments.length === 0) return;
  const rows = attachments.map((a) => ({
    message_id: messageId,
    storage_path: a.storage_path,
    file_name: a.file_name,
    mime_type: a.mime_type || null,
    size_bytes: a.size_bytes || null,
    uploaded_by: uploadedBy,
  }));
  const { error } = await supabase.from('attachments').insert(rows);
  if (error) throw error;
}

// Cambia el estado del hilo a través del RPC, que valida permisos y transición
// (asignado → resuelto; creador → terminada / reabrir / rechazar).
export async function setEstado(task_id: string, estado: TaskEstado): Promise<void> {
  const { error } = await supabase.rpc('set_task_estado', { p_task_id: task_id, p_estado: estado });
  if (error) throw error;
}

// --- Etapas del tablero (kanban) ---
// Un juego de etapas por espacio (supabase/sql/team-stages.sql). Las leen
// todos los miembros; solo los admins las crean, editan, ordenan o borran.
export function useTeamStages(): { stages: Stage[]; loading: boolean; refetch: () => Promise<void> } {
  const [stages, setStages] = useState<Stage[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    const active = getActiveTeamId();
    if (!active) { setStages([]); setLoading(false); return; }
    const { data } = await supabase
      .from('team_stages')
      .select('id, nombre, color, position')
      .eq('team_id', active)
      .order('position')
      .order('created_at');
    setStages((data ?? []) as Stage[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
    const active = getActiveTeamId();
    if (!active) return undefined;
    const ch = supabase
      .channel(`team-stages-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'team_stages' }, () => fetchAll())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [fetchAll]);

  return { stages, loading, refetch: fetchAll };
}

export async function createStage(input: { nombre: string; color: string; position: number }): Promise<void> {
  const active = getActiveTeamId();
  const { error } = await supabase
    .from('team_stages')
    .insert({ team_id: active, nombre: input.nombre.trim(), color: input.color, position: input.position });
  if (error) throw error;
}

export async function updateStage(stageId: string, patch: { nombre?: string; color?: string }): Promise<void> {
  const out: Record<string, unknown> = {};
  if (patch.nombre !== undefined) out.nombre = patch.nombre.trim();
  if (patch.color !== undefined) out.color = patch.color;
  if (Object.keys(out).length === 0) return;
  const { error } = await supabase.from('team_stages').update(out).eq('id', stageId);
  if (error) throw error;
}

// Los hilos que estuvieran en la etapa vuelven a "Sin etapa" (FK set null).
export async function deleteStage(stageId: string): Promise<void> {
  const { error } = await supabase.from('team_stages').delete().eq('id', stageId);
  if (error) throw error;
}

// Reescribe `position` según el orden recibido (ids de izquierda a derecha).
export async function reorderStages(orderedIds: string[]): Promise<void> {
  const results = await Promise.all(
    orderedIds.map((id, i) => supabase.from('team_stages').update({ position: i }).eq('id', id)),
  );
  const failed = results.find((r) => r.error);
  if (failed?.error) throw failed.error;
}

// Mueve un hilo de etapa (null = "Sin etapa"). Solo el asignado, o el creador
// si el hilo no tiene asignado: lo impone el trigger guard_task_stage.
export async function setTaskStage(task_id: string, stageId: string | null): Promise<void> {
  const { error } = await supabase.rpc('set_task_stage', { p_task_id: task_id, p_stage_id: stageId });
  if (error) throw error;
}

// El asignado fija (o limpia con null) su fecha personal para la Agenda.
// El RPC valida que sea el asignado y que no supere el deadline del asignador.
export async function setAssigneeDue(task_id: string, due: string | null): Promise<void> {
  const { error } = await supabase.rpc('set_assignee_due', { p_task_id: task_id, p_due: due });
  if (error) throw error;
}

export interface UpdateTaskPatch {
  asunto?: string;
  assigned_to?: string | null;
  fecha_vencimiento?: string | null;
}

export async function updateTask(task_id: string, patch: UpdateTaskPatch): Promise<void> {
  const out: Record<string, unknown> = {};
  if (patch.asunto !== undefined) out.asunto = patch.asunto;
  if (patch.assigned_to !== undefined) out.assigned_to = patch.assigned_to || null;
  if (patch.fecha_vencimiento !== undefined) out.fecha_vencimiento = patch.fecha_vencimiento || null;
  const { error } = await supabase.from('tasks').update(out).eq('id', task_id);
  if (error) throw error;
}

export async function updateMessage(
  message_id: string,
  { body }: { body: string },
): Promise<void> {
  const { error } = await supabase
    .from('messages')
    .update({ body, edited_at: new Date().toISOString() })
    .eq('id', message_id);
  if (error) throw error;
}

// Alterna una reacción (emoji) sobre un mensaje: la quita si ya estaba puesta
// por el usuario, o la añade si no. La RLS de message_reactions garantiza que
// el delete solo afecta a la fila propia (user_id = auth.uid()) y el insert usa
// el default user_id = auth.uid().
export async function toggleReaction(
  message_id: string,
  emoji: string,
  hasReacted: boolean,
): Promise<void> {
  if (hasReacted) {
    const { error } = await supabase
      .from('message_reactions')
      .delete()
      .eq('message_id', message_id)
      .eq('emoji', emoji);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('message_reactions')
      .insert({ message_id, emoji });
    if (error) throw error;
  }
}

export async function deleteMessage(message_id: string): Promise<void> {
  // Delega en la Edge Function `delete-message`, que limpia del bucket las
  // imágenes inline y los adjuntos antes de borrar la fila (cascade elimina
  // los attachments). La RLS exige que sea el autor del mensaje.
  const { data, error } = await supabase.functions.invoke('delete-message', {
    body: { message_id },
  });
  if (error) throw error;
  if (data && typeof data === 'object' && 'error' in data && data.error) {
    throw new Error(String((data as { error: unknown }).error));
  }
}

export async function deleteTask(task_id: string): Promise<void> {
  // Delega en la Edge Function `delete-task` que recolecta storage paths,
  // los borra del bucket y elimina la tarea (cascade). Si el usuario cierra
  // la pestaña a mitad, el servidor termina la limpieza igual.
  const { data, error } = await supabase.functions.invoke('delete-task', {
    body: { task_id },
  });
  if (error) throw error;
  if (data && typeof data === 'object' && 'error' in data && data.error) {
    throw new Error(String((data as { error: unknown }).error));
  }
}

// --- Guardados ---
export function useIsSaved(
  taskId: string | undefined,
  userId: string | null | undefined,
): { saved: boolean; toggle: () => Promise<void> } {
  const [saved, setSaved] = useState(false);

  const refresh = useCallback(async () => {
    if (!taskId || !userId) return;
    const { data } = await supabase
      .from('saved_tasks')
      .select('task_id')
      .eq('user_id', userId)
      .eq('task_id', taskId)
      .maybeSingle();
    setSaved(!!data);
  }, [taskId, userId]);

  useEffect(() => { refresh(); }, [refresh]);

  const toggle = useCallback(async () => {
    if (!taskId || !userId) return;
    if (saved) {
      await supabase.from('saved_tasks').delete().eq('user_id', userId).eq('task_id', taskId);
    } else {
      await supabase.from('saved_tasks').insert({ user_id: userId, task_id: taskId });
    }
    refresh();
  }, [saved, taskId, userId, refresh]);

  return { saved, toggle };
}


// --- Menciones ---


// Supabase JS no permite IN (subquery) directo: traemos los message_ids del
// hilo y los pasamos al filtro `.in()`.
async function messageIdsForTask(taskId: string): Promise<string[]> {
  const { data } = await supabase
    .from('messages')
    .select('id')
    .eq('task_id', taskId);
  return ((data ?? []) as Array<{ id: string }>).map((r) => r.id);
}

export async function markMentionsReadInTask(taskId: string, userId: string): Promise<void> {
  const ids = await messageIdsForTask(taskId);
  if (ids.length === 0) return;
  await supabase
    .from('mentions')
    .update({ read_at: new Date().toISOString() })
    .eq('mentioned_user_id', userId)
    .is('read_at', null)
    .in('message_id', ids);
}

export async function setTaskMentionsUnread(taskId: string, userId: string): Promise<void> {
  const ids = await messageIdsForTask(taskId);
  if (ids.length === 0) return;
  await supabase
    .from('mentions')
    .update({ read_at: null })
    .eq('mentioned_user_id', userId)
    .in('message_id', ids);
}


// --- Actividad reciente (campana de notificaciones) ---

export interface ActivityItem {
  taskId: string;
  taskAsunto: string;
  authorId: string;
  authorName: string;
  authorColor: string;
  bodySnippet: string;
  createdAt: string;
  unread: boolean;
  // 'message' = alguien escribió en el hilo; 'follow' = te añadieron como seguidor.
  kind: 'message' | 'follow';
}

export interface RecentActivityResult {
  unreadCount: number;
  unread: ActivityItem[];
  read: ActivityItem[];
  loading: boolean;
  refetch: () => Promise<void>;
}

const READ_HISTORY_LIMIT = 20;

function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').trim();
}

// Resumen "X habló en el hilo Y" deduplicado por hilo (último mensaje ajeno
// gana). Partido en dos:
//   - unread: hilos cuyo último mensaje ajeno es posterior a mi last_read_at
//   - read: hilos cuyo último mensaje ajeno ya está leído (limit 20)
// La campana muestra ambas secciones y el badge cuenta solo las unread.
export function useRecentActivity(userId: string | null | undefined): RecentActivityResult {
  const [unread, setUnread] = useState<ActivityItem[]>([]);
  const [read, setRead] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!userId) {
      setUnread([]);
      setRead([]);
      setLoading(false);
      return;
    }
    const active = getActiveTeamId();
    if (!active) { setUnread([]); setRead([]); setLoading(false); return; }
    const [{ data: msgRows }, { data: reads }, { data: followRows }] = await Promise.all([
      supabase
        .from('messages')
        .select(`
          id, task_id, author_id, body, created_at,
          author:profiles!messages_author_id_fkey(id, nombre, color),
          task:tasks!inner(id, asunto, team_id)
        `)
        .neq('author_id', userId)
        .order('created_at', { ascending: false })
        .limit(300),
      supabase
        .from('task_reads')
        .select('task_id, last_read_at')
        .eq('user_id', userId),
      supabase
        .from('task_followers')
        .select(`
          created_at, added_by,
          task:tasks!inner(id, asunto, team_id),
          adder:profiles!task_followers_added_by_fkey(id, nombre, color)
        `)
        .eq('user_id', userId)
        .neq('added_by', userId)
        .order('created_at', { ascending: false })
        .limit(100),
    ]);

    const readMap = new Map<string, string>();
    ((reads ?? []) as Array<{ task_id: string; last_read_at: string }>).forEach((r) => {
      readMap.set(r.task_id, r.last_read_at);
    });

    type Row = {
      id: string;
      task_id: string;
      author_id: string;
      body: string | null;
      created_at: string;
      author: { id: string; nombre: string; color: string } | null;
      task: { id: string; asunto: string; team_id: string } | null;
    };
    const rows = ((msgRows ?? []) as unknown) as Row[];

    const byTask = new Map<string, ActivityItem>();
    for (const r of rows) {
      if (!r.task || !r.author || r.task.team_id !== active) continue;  // solo espacio activo
      if (byTask.has(r.task_id)) continue; // primero gana (más reciente)
      const last = readMap.get(r.task_id);
      const isUnread = !last || r.created_at > last;
      byTask.set(r.task_id, {
        taskId: r.task_id,
        taskAsunto: r.task.asunto,
        authorId: r.author.id,
        authorName: r.author.nombre,
        authorColor: r.author.color,
        bodySnippet: stripHtml(r.body).slice(0, 120),
        createdAt: r.created_at,
        unread: isUnread,
        kind: 'message',
      });
    }

    // Items "te añadieron a un hilo" (uno por hilo, el más reciente). Mismo
    // criterio de no-leído que los mensajes: no leído si el follow es posterior
    // a mi last_read_at del hilo.
    type Prof = { id: string; nombre: string; color: string };
    type FRow = {
      created_at: string;
      added_by: string;
      task: { id: string; asunto: string; team_id: string } | { id: string; asunto: string; team_id: string }[] | null;
      adder: Prof | Prof[] | null;
    };
    const fRows = ((followRows ?? []) as unknown) as FRow[];
    const followByTask = new Map<string, ActivityItem>();
    for (const r of fRows) {
      const task = Array.isArray(r.task) ? r.task[0] : r.task;
      const adder = Array.isArray(r.adder) ? r.adder[0] : r.adder;
      if (!task || !adder || task.team_id !== active) continue;
      if (followByTask.has(task.id)) continue;
      const last = readMap.get(task.id);
      const isUnread = !last || r.created_at > last;
      followByTask.set(task.id, {
        taskId: task.id,
        taskAsunto: task.asunto,
        authorId: adder.id,
        authorName: adder.nombre,
        authorColor: adder.color,
        bodySnippet: 'Te ha añadido a este hilo',
        createdAt: r.created_at,
        unread: isUnread,
        kind: 'follow',
      });
    }

    const all = [...byTask.values(), ...followByTask.values()].sort((a, b) =>
      b.createdAt.localeCompare(a.createdAt),
    );
    setUnread(all.filter((i) => i.unread));
    setRead(all.filter((i) => !i.unread).slice(0, READ_HISTORY_LIMIT));
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchAll();
    if (!userId) return undefined;
    const ch = supabase
      .channel(`activity-${userId}-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, () => fetchAll())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_reads', filter: `user_id=eq.${userId}` }, () => fetchAll())
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'task_followers', filter: `user_id=eq.${userId}` }, () => fetchAll())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [fetchAll, userId]);

  return { unreadCount: unread.length, unread, read, loading, refetch: fetchAll };
}

export async function markTaskRead(taskId: string, userId: string): Promise<void> {
  await supabase.from('task_reads').upsert(
    { user_id: userId, task_id: taskId, last_read_at: new Date().toISOString() },
    { onConflict: 'user_id,task_id' },
  );
}

// Lee el sello "última vez que vi este hilo" antes de que el auto-mark-read
// del componente lo pise. Sirve para pintar las respuestas posteriores como
// "no leídas" la primera vez que entras al hilo desde la campana.
export async function getTaskLastReadAt(
  taskId: string,
  userId: string,
): Promise<string | null> {
  const { data } = await supabase
    .from('task_reads')
    .select('last_read_at')
    .eq('user_id', userId)
    .eq('task_id', taskId)
    .maybeSingle();
  return (data as { last_read_at: string } | null)?.last_read_at ?? null;
}

// Marca el hilo como no leído desde un mensaje concreto: pone last_read_at
// justo antes de su created_at, así ese mensaje y todos los posteriores
// vuelven a aparecer como no leídos en la campana.
export async function markTaskUnreadFrom(
  taskId: string,
  userId: string,
  messageCreatedAt: string,
): Promise<void> {
  const ts = new Date(messageCreatedAt);
  ts.setMilliseconds(ts.getMilliseconds() - 1);
  await supabase.from('task_reads').upsert(
    { user_id: userId, task_id: taskId, last_read_at: ts.toISOString() },
    { onConflict: 'user_id,task_id' },
  );
}

// Acuse de lectura del ASIGNADO (estilo WhatsApp) para una lista de hilos.
// Para cada hilo cuyo responsable NO sea el propio usuario, indica si ese
// responsable ya abrió el hilo (`read`) y cuándo (`at`). Los hilos sin asignar
// o asignados a uno mismo no entran en el mapa (no se pinta tick).
//
// Se apoya en la política RLS `assignee task_reads visible to team` (ver
// supabase/sql/task-read-receipts.sql), que expone únicamente la fila del
// responsable; las filas de seguidores siguen siendo privadas. Refresca en vivo
// vía realtime cuando el asignado abre uno de estos hilos.
export interface AssigneeReceipt {
  read: boolean;
  at: string | null;
}

export function useAssigneeReadReceipts(
  tasks: Task[],
  viewerId: string | null | undefined,
): Record<string, AssigneeReceipt> {
  // taskId -> last_read_at del responsable.
  const [reads, setReads] = useState<Record<string, string>>({});

  // Solo los hilos con responsable distinto a quien mira llevan tick.
  const relevant = useMemo(
    () => tasks.filter((t) => t.assigned_to && t.assigned_to !== viewerId),
    [tasks, viewerId],
  );
  const assigneeOf = useMemo(
    () => new Map(relevant.map((t) => [t.id, t.assigned_to as string])),
    [relevant],
  );
  // Clave estable: cambia solo si cambia el conjunto hilo→responsable, no en
  // cada render (evita re-suscribir realtime sin motivo).
  const idsKey = useMemo(
    () => relevant.map((t) => `${t.id}:${t.assigned_to}`).sort().join(','),
    [relevant],
  );

  const fetchReads = useCallback(async () => {
    const ids = [...assigneeOf.keys()];
    if (ids.length === 0) { setReads({}); return; }
    const { data } = await supabase
      .from('task_reads')
      .select('task_id, user_id, last_read_at')
      .in('task_id', ids);
    const map: Record<string, string> = {};
    ((data ?? []) as Array<{ task_id: string; user_id: string; last_read_at: string }>).forEach((r) => {
      // La RLS ya filtra, pero confirmamos que es la fila del responsable.
      if (assigneeOf.get(r.task_id) === r.user_id) map[r.task_id] = r.last_read_at;
    });
    setReads(map);
  }, [idsKey]);

  useEffect(() => {
    fetchReads();
    if (!idsKey) return undefined;
    const ids = new Set(idsKey.split(',').map((s) => s.split(':')[0]));
    const ch = supabase
      .channel(`receipts-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_reads' }, (payload) => {
        const row = (payload.new ?? payload.old) as { task_id?: string } | null;
        if (row?.task_id && ids.has(row.task_id)) fetchReads();
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [fetchReads, idsKey]);

  return useMemo(() => {
    const out: Record<string, AssigneeReceipt> = {};
    relevant.forEach((t) => {
      const at = reads[t.id] ?? null;
      out[t.id] = { read: !!at, at };
    });
    return out;
  }, [relevant, reads]);
}

// Acuse de lectura por RESPUESTA dentro de un hilo (el "ojito" de cada nota).
// Devuelve, para los participantes indicados (asignado + seguidores), su
// last_read_at en este hilo. Quién ha visto una nota concreta = participantes
// cuyo last_read_at es posterior al created_at del mensaje (se calcula en el
// componente). Se apoya en la política RLS `participant task_reads visible to
// team` (supabase/sql/task-read-receipts.sql), que expone las filas del asignado
// y de los seguidores a los miembros del equipo. Refresca en vivo cuando
// cualquiera abre el hilo (upsert en task_reads).
export function useThreadReads(
  taskId: string | undefined,
  participantIds: string[],
): Record<string, string> {
  const [reads, setReads] = useState<Record<string, string>>({});
  // Clave estable: solo cambia si cambia el conjunto de participantes.
  const idsKey = useMemo(() => [...participantIds].sort().join(','), [participantIds]);

  const fetchReads = useCallback(async () => {
    if (!taskId || !idsKey) { setReads({}); return; }
    const ids = idsKey.split(',');
    const { data } = await supabase
      .from('task_reads')
      .select('user_id, last_read_at')
      .eq('task_id', taskId)
      .in('user_id', ids);
    const map: Record<string, string> = {};
    ((data ?? []) as Array<{ user_id: string; last_read_at: string }>).forEach((r) => {
      map[r.user_id] = r.last_read_at;
    });
    setReads(map);
  }, [taskId, idsKey]);

  useEffect(() => {
    fetchReads();
    if (!taskId) return undefined;
    const ch = supabase
      .channel(`thread-reads-${taskId}-${Math.random().toString(36).slice(2, 8)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'task_reads', filter: `task_id=eq.${taskId}` },
        () => fetchReads(),
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [fetchReads, taskId]);

  return reads;
}


// --- Tags ---

export interface Tag {
  id: string;
  owner_id: string;
  nombre: string;
  color: string;
}

export interface TagWithCount {
  id: string;
  nombre: string;
  color: string;
  tasksCount: number;
}

// Tags mías que tienen al menos 1 hilo VISIBLE (RLS aplicada vía join).
// Se usa en el sidebar — las huérfanas no aparecen.
export function useMyActiveTags(
  userId: string | null | undefined,
): { tags: TagWithCount[]; loading: boolean } {
  const [tags, setTags] = useState<TagWithCount[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!userId) {
      setTags([]);
      setLoading(false);
      return;
    }
    const active = getActiveTeamId();
    if (!active) { setTags([]); setLoading(false); return; }
    // Tags (privadas, por espacio) + sus hilos y recordatorios. Los hilos se
    // filtran al espacio activo por team_id (la RLS por membresía trae los de
    // todos mis espacios). Los recordatorios son privados: la RLS los limita
    // a los míos.
    const [{ data }, { data: remLinks }] = await Promise.all([
      supabase
        .from('tags')
        .select('id, nombre, color, task_tags(task:tasks!inner(id, estado, team_id))')
        .eq('owner_id', userId)
        .eq('team_id', active)
        .order('nombre'),
      supabase
        .from('reminder_tags')
        .select('tag_id, reminder:reminders!inner(done, team_id)'),
    ]);

    type Row = {
      id: string;
      nombre: string;
      color: string;
      task_tags: Array<{ task: { id: string; estado: TaskEstado; team_id: string } | null }>;
    };
    const rows = (data ?? []) as unknown as Row[];

    // Recordatorios pendientes (no hechos) del espacio activo, contados por tag.
    type RemLink = { tag_id: string; reminder: { done: boolean; team_id: string } | null };
    const remCount: Record<string, number> = {};
    ((remLinks ?? []) as unknown as RemLink[]).forEach((l) => {
      if (l.reminder && l.reminder.team_id === active && !l.reminder.done) {
        remCount[l.tag_id] = (remCount[l.tag_id] ?? 0) + 1;
      }
    });

    // El tag aparece si tiene ≥1 hilo EN CURSO o ≥1 recordatorio pendiente en el
    // espacio activo, igual que las personas (openCount>0). El badge suma ambos.
    // Las tags sin nada en curso no ensucian el sidebar.
    const list: TagWithCount[] = rows
      .map((r) => {
        const tasks = (r.task_tags ?? [])
          .map((tt) => tt.task)
          .filter((t): t is { id: string; estado: TaskEstado; team_id: string } => t != null && t.team_id === active);
        return {
          id: r.id,
          nombre: r.nombre,
          color: r.color,
          tasksCount: tasks.filter((t) => t.estado === 'en_curso').length + (remCount[r.id] ?? 0),
        };
      })
      .filter((t) => t.tasksCount > 0);

    setTags(list);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchAll();
    if (!userId) return undefined;
    const ch = supabase
      .channel(`my-active-tags-${userId}-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tags', filter: `owner_id=eq.${userId}` }, () => fetchAll())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_tags' }, () => fetchAll())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, () => fetchAll())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'reminders', filter: `user_id=eq.${userId}` }, () => fetchAll())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'reminder_tags' }, () => fetchAll())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [fetchAll, userId]);

  return { tags, loading };
}

// Todas mis tags (con contador, incluso 0) — para la página de perfil
// donde puedo gestionarlas.
export function useMyAllTags(
  userId: string | null | undefined,
): { tags: TagWithCount[]; loading: boolean; refetch: () => Promise<void> } {
  const [tags, setTags] = useState<TagWithCount[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!userId) {
      setTags([]);
      setLoading(false);
      return;
    }
    const active = getActiveTeamId();
    if (!active) { setTags([]); setLoading(false); return; }
    const { data } = await supabase
      .from('tags')
      .select('id, nombre, color, task_tags(task:tasks!inner(id, team_id))')
      .eq('owner_id', userId)
      .eq('team_id', active)
      .order('nombre');

    type Row = {
      id: string;
      nombre: string;
      color: string;
      task_tags: Array<{ task: { id: string; team_id: string } | null }>;
    };
    const rows = (data ?? []) as unknown as Row[];

    // Cuenta de hilos del espacio activo (las tags ya son de este espacio).
    setTags(rows.map((r) => ({
      id: r.id,
      nombre: r.nombre,
      color: r.color,
      tasksCount: (r.task_tags ?? []).filter((tt) => tt.task != null && tt.task.team_id === active).length,
    })));
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchAll();
    if (!userId) return undefined;
    const ch = supabase
      .channel(`my-all-tags-${userId}-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tags', filter: `owner_id=eq.${userId}` }, () => fetchAll())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_tags' }, () => fetchAll())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [fetchAll, userId]);

  return { tags, loading, refetch: fetchAll };
}

// Tags MÍAS aplicadas a un hilo concreto. Para el formulario de editar
// hilo y para el hero de TareaPage.
export function useTaskTags(
  taskId: string | undefined,
  userId: string | null | undefined,
): { tags: Tag[]; loading: boolean; refetch: () => Promise<void> } {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!taskId || !userId) {
      setTags([]);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from('task_tags')
      .select('tag:tags(id, owner_id, nombre, color)')
      .eq('task_id', taskId);
    const rows = (data ?? []) as unknown as Array<{ tag: Tag | null }>;
    setTags(rows.map((r) => r.tag).filter((t): t is Tag => t != null && t.owner_id === userId));
    setLoading(false);
  }, [taskId, userId]);

  useEffect(() => {
    fetchAll();
    if (!taskId) return undefined;
    const ch = supabase
      .channel(`task-tags-${taskId}-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_tags', filter: `task_id=eq.${taskId}` }, () => fetchAll())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [fetchAll, taskId]);

  return { tags, loading, refetch: fetchAll };
}

// Hilos que tienen esta tag aplicada. Como las task_tags y el filtro por
// tag_id viven en otra tabla, no podemos reutilizar useTasksList (que se
// queda en supabase.from('tasks')). Usamos el patrón de embed con !inner:
// PostgREST joinará task_tags y filtrará por su tag_id.
export function useTasksByTag(tagId: string): TasksListResult {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!tagId) {
      setTasks([]);
      setLoading(false);
      return;
    }
    const active = getActiveTeamId();
    if (!active) { setTasks([]); setLoading(false); return; }
    const { data } = await supabase
      .from('tasks')
      .select(`${TASK_FIELDS}, task_tags!inner(tag_id)`)
      .eq('team_id', active)
      .eq('task_tags.tag_id', tagId)
      .order('updated_at', { ascending: false });
    setTasks((data ?? []) as unknown as Task[]);
    setLoading(false);
  }, [tagId]);

  useEffect(() => {
    fetchAll();
    if (!tagId) return undefined;
    const active = getActiveTeamId();
    const ch = supabase
      .channel(`tasks-by-tag-${tagId}-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks', filter: `team_id=eq.${active}` }, () => fetchAll())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_tags', filter: `tag_id=eq.${tagId}` }, () => fetchAll())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [fetchAll, tagId]);

  return { tasks, loading, refetch: fetchAll };
}

// --- Seguidores de un hilo ("Etiquetar") ---
// Gente extra que sigue el hilo (además del asignado): recibe avisos y lo
// encuentra en su vista "Siguiendo". Cualquier miembro del equipo puede
// añadir/quitar (RLS). El alta dispara email/push tipo 'follow' (trigger).
export interface Follower {
  id: string; // id de perfil del seguidor
  nombre: string;
  color: string;
  added_by: string;
}

export function useTaskFollowers(
  taskId: string | undefined,
): { followers: Follower[]; loading: boolean; refetch: () => Promise<void> } {
  const [followers, setFollowers] = useState<Follower[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!taskId) { setFollowers([]); setLoading(false); return; }
    const { data } = await supabase
      .from('task_followers')
      .select('added_by, profile:profiles!task_followers_user_id_fkey(id, nombre, color)')
      .eq('task_id', taskId);
    type Prof = { id: string; nombre: string; color: string };
    type Row = { added_by: string; profile: Prof | Prof[] | null };
    const list = ((data ?? []) as unknown as Row[])
      .map((r) => {
        const p = Array.isArray(r.profile) ? r.profile[0] : r.profile;
        return p ? { id: p.id, nombre: p.nombre, color: p.color, added_by: r.added_by } : null;
      })
      .filter((f): f is Follower => f != null)
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
    setFollowers(list);
    setLoading(false);
  }, [taskId]);

  useEffect(() => {
    fetchAll();
    if (!taskId) return undefined;
    const ch = supabase
      .channel(`task-followers-${taskId}-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_followers', filter: `task_id=eq.${taskId}` }, () => fetchAll())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [fetchAll, taskId]);

  return { followers, loading, refetch: fetchAll };
}

// Alta/baja idempotente: el cliente NO envía added_by (lo pone el default
// auth.uid(); enviarlo mal daría 403 por la política de insert).
export async function addFollower(taskId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('task_followers')
    .upsert({ task_id: taskId, user_id: userId }, { onConflict: 'task_id,user_id', ignoreDuplicates: true });
  if (error) throw error;
}

export async function removeFollower(taskId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('task_followers')
    .delete()
    .eq('task_id', taskId)
    .eq('user_id', userId);
  if (error) throw error;
}

export async function addFollowers(taskId: string, userIds: string[]): Promise<void> {
  if (userIds.length === 0) return;
  const rows = userIds.map((u) => ({ task_id: taskId, user_id: u }));
  const { error } = await supabase
    .from('task_followers')
    .upsert(rows, { onConflict: 'task_id,user_id', ignoreDuplicates: true });
  if (error) throw error;
}


// --- Grupos personales de miembros ---
// Cada usuario crea sus grupos de personas (privados) para etiquetar seguidores
// en bloque. Espeja el sistema de tags, pero con team_id (un grupo de gente solo
// tiene sentido dentro de un equipo) y con miembros (user_id) en vez de hilos.
export interface MemberGroup {
  id: string;
  nombre: string;
  color: string;
  userIds: string[];
}

export function useMyMemberGroups(
  userId: string | null | undefined,
): { groups: MemberGroup[]; loading: boolean; refetch: () => Promise<void> } {
  const [groups, setGroups] = useState<MemberGroup[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!userId) { setGroups([]); setLoading(false); return; }
    const active = getActiveTeamId();
    if (!active) { setGroups([]); setLoading(false); return; }
    const { data } = await supabase
      .from('member_groups')
      .select('id, nombre, color, group_members(user_id)')
      .eq('owner_id', userId)
      .eq('team_id', active)
      .order('nombre');
    type Row = { id: string; nombre: string; color: string; group_members: Array<{ user_id: string }> };
    const rows = (data ?? []) as unknown as Row[];
    setGroups(rows.map((r) => ({
      id: r.id,
      nombre: r.nombre,
      color: r.color,
      userIds: (r.group_members ?? []).map((m) => m.user_id),
    })));
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchAll();
    if (!userId) return undefined;
    const ch = supabase
      .channel(`my-member-groups-${userId}-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'member_groups', filter: `owner_id=eq.${userId}` }, () => fetchAll())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'group_members' }, () => fetchAll())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [fetchAll, userId]);

  return { groups, loading, refetch: fetchAll };
}

export interface CreateMemberGroupInput {
  nombre: string;
  color?: string;
  userIds: string[];
}

export async function createMemberGroup(
  input: CreateMemberGroupInput,
  ownerId: string,
): Promise<MemberGroup> {
  const active = getActiveTeamId();
  const { data, error } = await supabase
    .from('member_groups')
    .insert({ owner_id: ownerId, team_id: active, nombre: input.nombre.trim(), color: input.color ?? '#64748b' })
    .select('id, nombre, color')
    .single();
  if (error) throw error;
  const group = data as { id: string; nombre: string; color: string };
  if (input.userIds.length > 0) {
    const { error: e2 } = await supabase
      .from('group_members')
      .insert(input.userIds.map((user_id) => ({ group_id: group.id, user_id })));
    if (e2) throw e2;
  }
  return { id: group.id, nombre: group.nombre, color: group.color, userIds: input.userIds };
}

export async function updateMemberGroup(
  groupId: string,
  patch: { nombre?: string; color?: string },
): Promise<void> {
  const out: Record<string, unknown> = {};
  if (patch.nombre !== undefined) out.nombre = patch.nombre.trim();
  if (patch.color !== undefined) out.color = patch.color;
  if (Object.keys(out).length === 0) return;
  const { error } = await supabase.from('member_groups').update(out).eq('id', groupId);
  if (error) throw error;
}

export async function deleteMemberGroup(groupId: string): Promise<void> {
  // group_members cae en cascade.
  const { error } = await supabase.from('member_groups').delete().eq('id', groupId);
  if (error) throw error;
}

// Reemplaza el set de miembros de un grupo (diff add/remove), como setMyTaskTags.
export async function setGroupMembers(groupId: string, userIds: string[]): Promise<void> {
  const { data: existing } = await supabase
    .from('group_members')
    .select('user_id')
    .eq('group_id', groupId);
  const current = ((existing ?? []) as Array<{ user_id: string }>).map((r) => r.user_id);
  const desired = new Set(userIds);
  const toRemove = current.filter((id) => !desired.has(id));
  const toAdd = userIds.filter((id) => !current.includes(id));

  if (toRemove.length > 0) {
    const { error } = await supabase
      .from('group_members')
      .delete()
      .eq('group_id', groupId)
      .in('user_id', toRemove);
    if (error) throw error;
  }
  if (toAdd.length > 0) {
    const { error } = await supabase
      .from('group_members')
      .insert(toAdd.map((user_id) => ({ group_id: groupId, user_id })));
    if (error) throw error;
  }
}

// Mutaciones de tag
export interface CreateTagInput {
  nombre: string;
  color?: string;
}

export async function createTag(input: CreateTagInput, ownerId: string): Promise<Tag> {
  // Las tags son por espacio: se crean en el espacio activo.
  const teamId = getActiveTeamId();
  if (!teamId) throw new Error('no hay espacio de trabajo activo');
  const { data, error } = await supabase
    .from('tags')
    .insert({
      owner_id: ownerId,
      team_id: teamId,
      nombre: input.nombre.trim(),
      color: input.color ?? '#64748b',
    })
    .select('id, owner_id, nombre, color')
    .single();
  if (error) throw error;
  return data as Tag;
}

export async function updateTag(
  tagId: string,
  patch: { nombre?: string; color?: string },
): Promise<void> {
  const out: Record<string, unknown> = {};
  if (patch.nombre !== undefined) out.nombre = patch.nombre.trim();
  if (patch.color !== undefined) out.color = patch.color;
  if (Object.keys(out).length === 0) return;
  const { error } = await supabase.from('tags').update(out).eq('id', tagId);
  if (error) throw error;
}

export async function deleteTag(tagId: string): Promise<void> {
  // Las task_tags asociadas caen en cascade.
  const { error } = await supabase.from('tags').delete().eq('id', tagId);
  if (error) throw error;
}

// Reemplaza el set completo de tags MÍAS aplicadas a un hilo.
// Mantiene las de otros usuarios intactas (no se tocan).
export async function setMyTaskTags(
  taskId: string,
  tagIds: string[],
  ownerId: string,
): Promise<void> {
  // 1. Mis tags actualmente vinculadas
  const { data: existing } = await supabase
    .from('task_tags')
    .select('tag_id, tag:tags!inner(owner_id)')
    .eq('task_id', taskId);
  type Row = { tag_id: string; tag: { owner_id: string } };
  const mineExisting = ((existing ?? []) as unknown as Row[])
    .filter((r) => r.tag.owner_id === ownerId)
    .map((r) => r.tag_id);

  const desired = new Set(tagIds);
  const toRemove = mineExisting.filter((id) => !desired.has(id));
  const toAdd = tagIds.filter((id) => !mineExisting.includes(id));

  if (toRemove.length > 0) {
    const { error } = await supabase
      .from('task_tags')
      .delete()
      .eq('task_id', taskId)
      .in('tag_id', toRemove);
    if (error) throw error;
  }
  if (toAdd.length > 0) {
    const { error } = await supabase
      .from('task_tags')
      .insert(toAdd.map((tag_id) => ({ task_id: taskId, tag_id })));
    if (error) throw error;
  }
}


// --- Tags de recordatorios ---
// Reutilizan la tabla `tags` (las mismas que los hilos). La unión vive en
// `reminder_tags`. Como los recordatorios son privados al dueño, todas las
// filas de reminder_tags de un recordatorio son suyas: no hay que preservar
// las de otros (a diferencia de task_tags).

// Tags aplicadas a un recordatorio concreto (para el formulario de edición).
export function useReminderTags(
  reminderId: string | undefined,
  enabled: boolean,
): { tags: Tag[]; loading: boolean } {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!reminderId || !enabled) {
      setTags([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    supabase
      .from('reminder_tags')
      .select('tag:tags(id, owner_id, nombre, color)')
      .eq('reminder_id', reminderId)
      .then(({ data }) => {
        if (cancelled) return;
        const rows = (data ?? []) as unknown as Array<{ tag: Tag | null }>;
        setTags(rows.map((r) => r.tag).filter((t): t is Tag => t != null));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [reminderId, enabled]);

  return { tags, loading };
}

// Reemplaza el set completo de tags aplicadas a un recordatorio.
export async function setMyReminderTags(
  reminderId: string,
  tagIds: string[],
): Promise<void> {
  const { data: existing } = await supabase
    .from('reminder_tags')
    .select('tag_id')
    .eq('reminder_id', reminderId);
  const current = ((existing ?? []) as Array<{ tag_id: string }>).map((r) => r.tag_id);

  const desired = new Set(tagIds);
  const toRemove = current.filter((id) => !desired.has(id));
  const toAdd = tagIds.filter((id) => !current.includes(id));

  if (toRemove.length > 0) {
    const { error } = await supabase
      .from('reminder_tags')
      .delete()
      .eq('reminder_id', reminderId)
      .in('tag_id', toRemove);
    if (error) throw error;
  }
  if (toAdd.length > 0) {
    const { error } = await supabase
      .from('reminder_tags')
      .insert(toAdd.map((tag_id) => ({ reminder_id: reminderId, tag_id })));
    if (error) throw error;
  }
}

// Mis recordatorios (pendientes y hechos) que tienen esta tag aplicada, en el
// espacio activo. Sirve para unificar la vista de TAGS con la Agenda: los
// recordatorios aparecen junto a los hilos. Trae todas las tags de cada uno
// para pintar sus chips.
export function useRemindersByTag(
  tagId: string,
  userId: string | null | undefined,
): { reminders: Reminder[]; loading: boolean; refetch: () => Promise<void> } {
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!tagId || !userId) { setReminders([]); setLoading(false); return; }
    const active = getActiveTeamId();
    if (!active) { setReminders([]); setLoading(false); return; }
    // 1. IDs de mis recordatorios con esta tag (la RLS limita a los míos).
    const { data: links } = await supabase
      .from('reminder_tags')
      .select('reminder_id')
      .eq('tag_id', tagId);
    const ids = ((links ?? []) as Array<{ reminder_id: string }>).map((l) => l.reminder_id);
    if (ids.length === 0) { setReminders([]); setLoading(false); return; }
    // 2. Recordatorios completos (con todas sus tags y conteo de adjuntos).
    const { data } = await supabase
      .from('reminders')
      .select('*, reminder_attachments(count), reminder_tags(tag:tags(id, nombre, color))')
      .eq('user_id', userId)
      .eq('team_id', active)
      .in('id', ids)
      .order('fecha_vencimiento', { ascending: true });
    setReminders(((data ?? []) as unknown[]).map(mapReminderRow));
    setLoading(false);
  }, [tagId, userId]);

  useEffect(() => {
    fetchAll();
    if (!tagId || !userId) return undefined;
    const ch = supabase
      .channel(`reminders-by-tag-${tagId}-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'reminders', filter: `user_id=eq.${userId}` }, () => fetchAll())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'reminder_tags', filter: `tag_id=eq.${tagId}` }, () => fetchAll())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [fetchAll, tagId, userId]);

  return { reminders, loading, refetch: fetchAll };
}


// Dado un conjunto de task_ids, devuelve para cada uno las tags MÍAS
// aplicadas. Sirve para derivar el set de tags disponibles en vistas
// como "hilos asignados a X".
export function useMyTagsForTasks(
  taskIds: string[],
  userId: string | null | undefined,
): { tagsByTask: Record<string, Tag[]>; loading: boolean } {
  const [tagsByTask, setTagsByTask] = useState<Record<string, Tag[]>>({});
  const [loading, setLoading] = useState(true);

  // Key estable: aplanar a string ordenada. Los efectos dependen del valor,
  // no de la referencia del array, así que arrays nuevos con mismos ids no
  // disparan refetch.
  const idsKey = [...taskIds].sort().join(',');

  useEffect(() => {
    let cancelled = false;
    if (!userId || idsKey === '') {
      setTagsByTask({});
      setLoading(false);
      return undefined;
    }
    const ids = idsKey.split(',');
    setLoading(true);
    supabase
      .from('task_tags')
      .select('task_id, tag:tags!inner(id, owner_id, nombre, color)')
      .in('task_id', ids)
      .eq('tag.owner_id', userId)
      .then(({ data }) => {
        if (cancelled) return;
        type Row = { task_id: string; tag: Tag | null };
        const rows = (data ?? []) as unknown as Row[];
        const map: Record<string, Tag[]> = {};
        rows.forEach((r) => {
          if (!r.tag) return;
          if (!map[r.task_id]) map[r.task_id] = [];
          map[r.task_id]!.push(r.tag);
        });
        setTagsByTask(map);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [userId, idsKey]);

  return { tagsByTask, loading };
}

// --- Bandeja "Todo": todos mis hilos con flag de no leído ---

// Hilo con la información de participación y lectura que devuelve la RPC
// `my_threads` (supabase/sql/my-threads.sql). Es un Task normal más flags.
export interface MyThread extends Task {
  is_assignee: boolean;
  is_creator: boolean;
  is_mentioned: boolean;
  is_follower: boolean;
  // Hay novedades que aún no he visto: mensaje ajeno posterior a mi última
  // lectura, me añadieron al hilo, hilo ajeno nunca abierto o mención sin leer.
  unread: boolean;
  unread_mentions: number;
  // Última novedad ajena en el hilo (null si solo he escrito yo).
  last_foreign_at: string | null;
}

export interface MyThreadsResult {
  threads: MyThread[];
  unreadCount: number;
  loading: boolean;
  refetch: () => Promise<void>;
}

// Todos los hilos del espacio activo en los que participo (asignado a mí,
// creado por mí, mencionado o seguidor). Una sola RPC; el orden por defecto es
// updated_at desc y `unreadCount` cuenta solo los hilos en curso no leídos
// (badge de Bandeja en el sidebar).
export function useMyThreads(userId: string | null | undefined): MyThreadsResult {
  const [threads, setThreads] = useState<MyThread[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!userId) { setThreads([]); setLoading(false); return; }
    const active = getActiveTeamId();
    if (!active) { setThreads([]); setLoading(false); return; }
    const { data, error } = await supabase.rpc('my_threads', { p_team_id: active });
    if (error) {
      console.error('my_threads', error);
      setLoading(false);
      return;
    }
    setThreads((data ?? []) as MyThread[]);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchAll();
    if (!userId) return undefined;
    const active = getActiveTeamId();
    // Cualquier cosa que cambie el listado o el estado de lectura: hilos del
    // espacio, mensajes nuevos, mis lecturas, mis seguimientos y mis menciones.
    const ch = supabase
      .channel(`my-threads-${userId}-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks', filter: `team_id=eq.${active}` }, () => fetchAll())
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, () => fetchAll())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_reads', filter: `user_id=eq.${userId}` }, () => fetchAll())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_followers', filter: `user_id=eq.${userId}` }, () => fetchAll())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'mentions', filter: `mentioned_user_id=eq.${userId}` }, () => fetchAll())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [fetchAll, userId]);

  const unreadCount = useMemo(
    () => threads.filter((t) => t.unread && t.estado === 'en_curso').length,
    [threads],
  );

  return { threads, unreadCount, loading, refetch: fetchAll };
}

// Marca un hilo como leído desde el listado (sin abrirlo): sello de lectura
// del hilo + menciones pendientes en él.
export async function markThreadRead(taskId: string, userId: string): Promise<void> {
  await Promise.all([markTaskRead(taskId, userId), markMentionsReadInTask(taskId, userId)]);
}

// Vuelve a marcar el hilo como no leído: retrasa mi sello de lectura justo
// antes de la última novedad ajena (`last_foreign_at` de la RPC) y reabre mis
// menciones en él. No se borra la fila de task_reads: la tabla no tiene
// política RLS de DELETE, así que el borrado se ignoraría en silencio.
export async function markThreadUnread(taskId: string, userId: string, lastForeignAt: string): Promise<void> {
  await Promise.all([
    markTaskUnreadFrom(taskId, userId, lastForeignAt),
    setTaskMentionsUnread(taskId, userId),
  ]);
}
